import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Users,
  User,
  UserCircle,
  CreditCard,
  DollarSign,
  TrendingUp,
  Tag,
  Settings,
  X,
  LogOut,
} from '../../../components/ui/icons';
import { useDispatch } from 'react-redux';
import { logout } from '../../../features/auth/authSlice';

const DashSidebar = ({ isOpen, toggleSidebar }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const sidebarActive = location.state?.sidebarActive;
  const isSurgeonDetailsPage = location.pathname.startsWith('/dash/surgeon-profiles/');

  const menuItems = [
    { path: '/dash', label: t('dash.sidebar.dashboard'), icon: Home, end: true },
    { path: '/dash/surgeons', label: t('dash.sidebar.requestedSurgeons'), icon: Users },
    { path: '/dash/users', label: t('dash.sidebar.users'), icon: UserCircle },
    { path: '/dash/pricing', label: t('dash.sidebar.pricingPlan'), icon: Users },
    { path: '/dash/subscriptions', label: t('dash.sidebar.subscriptions'), icon: CreditCard },
    { path: '/dash/revenue', label: t('dash.sidebar.revenue'), icon: DollarSign },
    { path: '/dash/reports', label: t('dash.sidebar.reportsAnalytics'), icon: TrendingUp },
    { path: '/dash/promo', label: t('dash.sidebar.promoCodes'), icon: Tag },
    { path: '/dash/settings', label: t('dash.sidebar.settings'), icon: Settings },
    { path: '/dash/profile', label: t('dash.sidebar.profile'), icon: User },
  ];

  const dispatch = useDispatch();

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const resolveIsActive = (item, navIsActive) => {
    // Details page is shared — keep the parent menu that opened it.
    if (isSurgeonDetailsPage) {
      if (sidebarActive) return sidebarActive === item.path;
      // Fallback when opened without state (e.g. refresh / direct URL)
      return item.path === '/dash/surgeons';
    }

    return navIsActive;
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 z-30 bg-black/80 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          isOpen ? 'visible opacity-50' : 'pointer-events-none invisible opacity-0'
        }`}
        onClick={toggleSidebar}
      />

      {/* Sidebar */}
      <aside
        style={{ willChange: 'transform' }}
        className={`fixed inset-y-0 left-0 z-40 flex w-[80%] max-w-xs transform-gpu flex-col bg-white shadow-xl transition-transform duration-500 ease-out lg:w-64 lg:max-w-none lg:translate-x-0 lg:border-r lg:border-gray-200 lg:shadow-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-2 py-3 lg:hidden">
          <div className="flex items-center gap-2">
            <img
              src="/image/logo.png"
              alt="TrustSurgery logo"
              loading="eager"
              decoding="async"
              width="48"
              height="48"
              className="h-12"
            />
          </div>
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label={t('dash.sidebar.closeSidebar')}
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Scrollable Menu */}
        <nav className="flex flex-1 flex-col overflow-y-auto p-4">
          <Link
            to="/"
            className="mb-4 hidden items-center justify-center gap-2 border-b border-gray-200 pb-2 md:flex"
          >
            <img
              src="/image/logo.png"
              alt="TrustSurgery logo"
              loading="lazy"
              decoding="async"
              width="70"
              height="50"
              className="h-8 lg:h-12"
            />
          </Link>

          <div className="flex flex-col space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  onClick={handleNavClick}
                  className={({ isActive }) => {
                    const active = resolveIsActive(item, isActive);
                    return `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200 ${
                      active
                        ? 'bg-[#026CB6] text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-[#143681]'
                    }`;
                  }}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>

          {/* Logout Button — pinned to bottom */}
          <div className="mt-auto border-t border-gray-200 pt-4">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-red-600 transition-all duration-200 hover:bg-red-50 hover:text-red-700"
            >
              <LogOut size={20} />
              <span>{t('dash.sidebar.logout')}</span>
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default DashSidebar;
